import React, { useEffect, useMemo } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { TfiControlRecord } from "react-icons/tfi";
import { SurveyCreator, SurveyCreatorComponent } from "survey-creator-react";
import "survey-core/survey-core.min.css";
import "survey-creator-core/survey-creator-core.min.css";
import { surveyConfig } from "../config/surveyConfig.js";
import { fetchSurveyDefinition } from "../services/surveyService";
import { registerChoiceItemProperties } from "../survey/registerChoiceItemProperties.js";

const creatorOptions = {
  showLogicTab: true,
  isAutoSave: true,
};

const defaultSurveyJson = {
  pages: [
    {
      name: "page1",
      elements: [
        {
          type: "radiogroup",
          name: "question1",
          title: "Jinsingizni ko'rsating",
          choices: [
            { value: "male", text: "Erkak" },
            { value: "female", text: "Ayol" },
          ],
        },
      ],
    },
  ],
  headerView: "advanced",
};

const iconChoiceTypes = new Set(["radiogroup", "checkbox"]);
const fallbackChoiceIcons = {
  q: renderToStaticMarkup(
    <TfiControlRecord className="sv-item-icon-svg sv-item-icon-fallback" aria-hidden="true" focusable="false" />
  ),
};

registerChoiceItemProperties();

function normalizeIconValue(iconVal) {
  if (Array.isArray(iconVal) && iconVal.length > 0) {
    const firstFile = iconVal[0];
    if (firstFile && firstFile.content) {
      const mime = firstFile.type || "image/png";
      return `data:${mime};base64,${firstFile.content}`;
    }
  }
  if (typeof iconVal === "string") return iconVal.trim();
  return "";
}

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(value) {
  return String(value).replace(/"/g, "&quot;");
}

function getIconHtml(iconVal) {
  const resolved = normalizeIconValue(iconVal);
  if (!resolved) return "";
  const fallbackIcon = fallbackChoiceIcons[resolved.toLowerCase()];
  if (fallbackIcon) return fallbackIcon;
  if (
    resolved.startsWith("http://") ||
    resolved.startsWith("https://") ||
    resolved.startsWith("data:") ||
    resolved.startsWith("/")
  ) {
    return `<img src="${escapeAttr(resolved)}" class="sv-item-icon-img" alt="" />`;
  }
  return `<i class="${escapeAttr(resolved)} sv-item-icon-class" aria-hidden="true"></i>`;
}

function findChoiceByText(question, renderedText) {
  return question.choices.find((choice) => {
    if (choice.text === renderedText) return true;
    if (choice.value === renderedText) return true;
    if (choice.locText && choice.locText.text === renderedText) return true;
    return false;
  });
}

function SurveyCreatorWidget() {
  const creator = useMemo(() => {
    const instance = new SurveyCreator(creatorOptions);
    instance.saveSurveyFunc = (saveNo, callback) => {
      callback(saveNo, true);
    };
    return instance;
  }, []);

  useEffect(() => {
    let isDisposed = false;

    const setCreatorJson = (json) => {
      creator.JSON = json;
    };

    fetchSurveyDefinition(surveyConfig.surveyId)
      .then((json) => {
        if (!isDisposed) setCreatorJson(json);
      })
      .catch(() => {
        if (!isDisposed) setCreatorJson(defaultSurveyJson);
      });

    const handleSurveyInstanceCreated = (_, options) => {
      options.survey.onTextMarkdown.add((__, markdownOptions) => {
        const question = markdownOptions.element;
        if (!question) return;
        if (markdownOptions.name !== "text") return;
        if (!question.choices || !question.choices.length) return;
        if (!iconChoiceTypes.has(question.getType())) return;

        const choice = findChoiceByText(question, markdownOptions.text);
        if (!choice || !choice.icon) return;

        const iconHtml = getIconHtml(choice.icon);
        if (!iconHtml) return;

        markdownOptions.html = `<span class="sv-choice-icon-wrapper">${iconHtml}<span>${escapeHtml(
          markdownOptions.text
        )}</span></span>`;
      });
    };

    creator.onSurveyInstanceCreated.add(handleSurveyInstanceCreated);

    return () => {
      isDisposed = true;
      creator.onSurveyInstanceCreated.remove(handleSurveyInstanceCreated);
    };
  }, [creator]);

  return <SurveyCreatorComponent creator={creator} />;
}

export default SurveyCreatorWidget;
